import { useState, useEffect } from 'react';
import { Box, Paper, Typography, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, setHours, setMinutes, isSameDay, addDays } from 'date-fns';
import { sportEventApi, matchApi } from '../../services/api';
import { useSnackbar } from 'notistack';
import { SportEvent, Match } from '../../models';
import EventDetailsDrawer from './components/EventDetailsDrawer';

interface TimeSlot {
  hour: number;
  minute: number;
}

interface Platform {
  id: 'd11' | 'c11' | 'mt11' | 'cb11' | 'ai11';
  name: string;
}

interface EventsState {
  d11: SportEvent[];
  c11: SportEvent[];
  mt11: SportEvent[];
  cb11: SportEvent[];
  ai11: Match[];
}

// Generate time slots for every 30 minutes
const TIME_SLOTS: TimeSlot[] = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = (i % 2) * 30;
  return { hour, minute };
});

const PLATFORMS: Platform[] = [
  { id: 'd11', name: 'Dream11' },
  { id: 'c11', name: 'My11Circle' },
  { id: 'mt11', name: 'MyTeam11' },
  { id: 'cb11', name: 'Cricbuzz11' },
  { id: 'ai11', name: 'AI11' }
];

const SportEvents: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedSport, setSelectedSport] = useState<'cricket' | 'football' | 'kabaddi' | 'basketball'>('cricket');
  const [events, setEvents] = useState<EventsState>({
    d11: [],
    c11: [],
    mt11: [],
    cb11: [],
    ai11: []
  });
  // const [loading, setLoading] = useState<boolean>(true);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [selectedEvent, setSelectedEvent] = useState<SportEvent | Match | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform['id'] | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const fetchEvents = async (): Promise<void> => {
    try {
      // setLoading(true);
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const nextDateStr = format(addDays(selectedDate, 1), 'yyyy-MM-dd');
      const start_time_after = `${dateStr}T00:00:00+05:30`;
      const start_time_before = `${nextDateStr}T00:00:00+05:30`;
      const [d11Res, c11Res, mt11Res, cb11Res, ai11Res] = await Promise.all([
        sportEventApi.getAll({
          platform: 'd11',
          sport: selectedSport,
          start_time_after,
          start_time_before,
          page_size: 100
        }),
        sportEventApi.getAll({
          platform: 'c11',
          sport: selectedSport,
          start_time_after,
          start_time_before,
          page_size: 100
        }),
        sportEventApi.getAll({
          platform: 'mt11',
          sport: selectedSport,
          start_time_after,
          start_time_before,
          page_size: 100
        }),
        sportEventApi.getAll({
          platform: 'cb11',
          sport: selectedSport,
          start_time_after,
          start_time_before,
          page_size: 100
        }),
        matchApi.getAll({
          start_time_after,
          start_time_before,
          page_size: 100
        })
      ]);

      setEvents({
        d11: d11Res.data.results || [],
        c11: c11Res.data.results || [],
        mt11: mt11Res.data.results || [],
        cb11: cb11Res.data.results || [],
        ai11: ai11Res.data.results || []
      });
    } catch (error) {
      console.error('Error fetching events:', error);
      enqueueSnackbar('Error fetching events', { variant: 'error' });
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedSport]);

  const getEventsForTimeSlot = (hour: number, minute: number, platform: Platform['id']): (SportEvent | Match)[] => {
    if (platform === 'ai11') {
      const platformEvents = events[platform] || [];
      return platformEvents.filter((event: Match) => {
        const eventTime = new Date(event.start_date);
        return eventTime.getHours() === hour && eventTime.getMinutes() >= minute && eventTime.getMinutes() < minute + 30;
      });
    } else {
      const platformEvents = events[platform] || [];
      return platformEvents.filter((event: SportEvent) => {
        const eventTime = new Date(event.start_time);
        const eventHour = eventTime.getHours();
        const eventMinute = eventTime.getMinutes();
        
        // Check if event falls within the current 30-minute slot
        if (eventHour === hour) {
          if (minute === 0) {
            return eventMinute < 30;
          } else {
            return eventMinute >= 30;
          }
        }
        return false;
      });
    }
  };

  const isCurrentTimeSlot = (hour: number, minute: number): boolean => {
    if (!isSameDay(currentTime, selectedDate)) return false;
    return currentTime.getHours() === hour && 
           currentTime.getMinutes() >= minute && 
           currentTime.getMinutes() < minute + 30;
  };

  const handleEventClick = (event: SportEvent | Match, platform: Platform['id']) => {
    setSelectedEvent(event);
    setSelectedPlatform(platform);
  };

  const handleCloseDrawer = () => {
    setSelectedEvent(null);
    setSelectedPlatform(null);
  };

  const renderEventContent = (event: SportEvent | Match, platform: Platform['id']) => {
    if (platform === 'ai11') {
      const matchEvent = event as Match;
      return (
        <>
        <Grid container>
          <Grid item xs={8}>
          <Typography variant="subtitle2" noWrap>
            {matchEvent.name || 'TBD vs TBD'}
          </Typography>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'right' }}>
          <Typography variant="body2" color="textDisabled">
            {matchEvent.id}
          </Typography>
          </Grid>
        </Grid>
          <Typography variant="caption" color="text.secondary" noWrap>
            {matchEvent.series?.name || ''}
          </Typography>
        </>
      );
    }
    const sportEvent = event as SportEvent;
    return (
      <>
      <Grid container>
        <Grid item xs={8}>
        <Typography variant="subtitle2" noWrap>
          {sportEvent.team1_name} vs {sportEvent.team2_name}
        </Typography>
        </Grid>
        <Grid item xs={4} sx={{ textAlign: 'right' }}>
        <Typography variant="body2" color="textDisabled">
            {sportEvent.c11_id ?? sportEvent.d11_id ?? sportEvent.mt11_id ?? sportEvent.cb11_id}
          </Typography>
        </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" noWrap>
          {sportEvent.series_name}
        </Typography>
      </>
    );
  };

  const renderTimeSlot = (timeSlot: TimeSlot) => {
    const { hour, minute } = timeSlot;
    const isCurrentSlot = isCurrentTimeSlot(hour, minute);
    const timeStr = format(setMinutes(setHours(new Date(), hour), minute), 'hh:mm a');

    return (
      <Grid item xs={12} key={`${hour}-${minute}`} sx={{ 
        display: 'flex', 
        borderBottom: '1px solid #eee',
        position: 'relative'
      }}>
        {isCurrentSlot && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              height: '2px',
              backgroundColor: 'error.main',
              zIndex: 1
            }}
          />
        )}
        <Box sx={{ width: 90, p: 1, borderRight: '1px solid #eee' }}>
          <Typography variant="body2" color="text.secondary">
            {timeStr}
          </Typography>
        </Box>
        {PLATFORMS.map(platform => {
          const slotEvents = getEventsForTimeSlot(hour, minute, platform.id);
          return (
            <Box
              key={platform.id}
              sx={{
                flex: 1,
                p: 1,
                borderRight: '1px solid #eee',
                bgcolor: slotEvents.length > 0 ? 'action.hover' : 'transparent'
              }}
            >
              {slotEvents.map((event, index) => {
                const hasMatchId = platform.id === 'ai11' ? true : (event as SportEvent).match_id;
                return (
                  <Box
                    key={event.id}
                    sx={{
                      cursor: 'pointer',
                      mb: index < slotEvents.length - 1 ? 1 : 0,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: hasMatchId ? 'background.paper' : 'error.lighter',
                      border: hasMatchId ? 'none' : '1px solid',
                      borderColor: 'error.main',
                      '&:hover': {
                        bgcolor: hasMatchId ? 'action.selected' : 'error.light'
                      }
                    }}
                    onClick={() => handleEventClick(event, platform.id)}
                  >
                    {renderEventContent(event, platform.id)}
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Grid>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Paper 
          sx={{ 
            p: 2, 
            mb: 3,
            position: 'sticky',
            top: 60,
            zIndex: 2,
            backgroundColor: 'background.paper'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h5" component="h1" gutterBottom>
                Sport Events
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={(newValue: Date | null) => newValue && setSelectedDate(newValue)}
                // renderInput={(params: any) => <TextField {...params} fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sport</InputLabel>
                <Select
                  value={selectedSport}
                  label="Sport"
                  onChange={(e) => setSelectedSport(e.target.value as typeof selectedSport)}
                >
                  <MenuItem value="cricket">Cricket</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Grid container>
            <Grid 
              item 
              xs={12} 
              sx={{ 
                display: 'flex', 
                borderBottom: '1px solid #eee',
                position: 'sticky',
                top: 150,
                zIndex: 20,
                backgroundColor: 'background.paper'
              }}
            >
              <Box sx={{ width: 90, p: 1, borderRight: '1px solid #eee' }}>
                <Typography variant="subtitle2">Time</Typography>
              </Box>
              {PLATFORMS.map(platform => (
                <Box
                  key={platform.id}
                  sx={{
                    flex: 1,
                    p: 1,
                    borderRight: '1px solid #eee',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="subtitle2">{platform.name}</Typography>
                </Box>
              ))}
            </Grid>
            {TIME_SLOTS.map(timeSlot => renderTimeSlot(timeSlot))}
          </Grid>
        </Paper>

        <EventDetailsDrawer
          open={!!selectedEvent}
          onClose={handleCloseDrawer}
          event={selectedEvent}
          platform={selectedPlatform || 'd11'}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default SportEvents; 